<template>
  <div
    class="course-mega-menu"
    @mouseenter="openOnHover"
    @mouseleave="scheduleMegaMenuClose"
  >
    <button
      type="button"
      class="header-enroll-btn mr-2 border-0 shadow-none course-mega-menu__trigger"
      aria-haspopup="true"
      :aria-expanded="isMegaMenuOpen ? 'true' : 'false'"
      @click="toggleMegaMenu"
    >
      <span>Courses</span>
      <svg
        class="course-mega-menu__trigger-icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        fill="none"
      >
        <circle
          cx="12"
          cy="12"
          r="8.5"
          stroke="currentColor"
          stroke-width="1.8"
        />
        <path
          d="M12 8V15"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />
        <path
          d="M9.5 12.5L12 15L14.5 12.5"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <transition name="course-mega-menu-fade">
      <div
        v-if="isMegaMenuOpen"
        class="course-mega-menu__panel"
        :class="toneClass"
        @mouseenter="cancelMegaMenuClose"
        @mouseleave="scheduleMegaMenuClose"
      >
        <div
          class="course-mega-menu__categories"
          aria-label="Course categories"
        >
          <button
            v-for="category in getCategories"
            :key="category.ProgramCategory.ProgramCategoryId"
            type="button"
            class="course-mega-menu__category"
            :class="{
              'course-mega-menu__category--active':
                category.ProgramCategory.ProgramCategoryId == activeCategoryId,
            }"
            :aria-pressed="
              category.ProgramCategory.ProgramCategoryId == activeCategoryId
                ? 'true'
                : 'false'
            "
            @mouseenter="selectCategory(category)"
            @focus="selectCategory(category)"
            @click="onCategoryClick(category)"
          >
            <span class="course-mega-menu__category-icon">
              <img
                v-if="category.ProgramCategory.ProgramCategoryICon"
                :src="category.ProgramCategory.ProgramCategoryICon"
                :alt="category.ProgramCategory.Category"
              />
            </span>

            <span class="course-mega-menu__category-title">
              {{ category.ProgramCategory.Category }}
            </span>
          </button>
        </div>

        <div class="course-mega-menu__levels">
          <button
            v-if="activeCategoryRecord"
            type="button"
            class="course-mega-menu__view-all"
            @click="goToCategory(activeCategoryRecord)"
          >
            View all {{ activeCategoryRecord.ProgramCategory.Category }}
          </button>
          <div
            v-if="primaryRecords.length"
            class="course-mega-menu__primary-list"
          >
            <section
              v-for="record in primaryRecords"
              :key="`${primaryNode.NodeKey}-${getNodeId(
                record,
                primaryNode.NodeKey,
              )}`"
              class="course-mega-menu__primary-section"
            >
              <button
                type="button"
                class="course-mega-menu__primary-heading"
                @click="goToPrimary(record)"
              >
                <span
                  v-if="getNodeIcon(record, primaryNode.NodeKey)"
                  class="course-mega-menu__option-icon"
                >
                  <img
                    :src="getNodeIcon(record, primaryNode.NodeKey)"
                    :alt="getNodeTitle(record, primaryNode.NodeKey)"
                  />
                </span>

                <span class="course-mega-menu__primary-label">
                  {{ getNodeTitle(record, primaryNode.NodeKey) }}
                </span>
              </button>

              <div
                v-if="getSecondaryRecords(record).length"
                class="course-mega-menu__secondary-grid"
              >
                <button
                  v-for="child in getSecondaryRecords(record)"
                  :key="`${secondaryNode.NodeKey}-${getNodeId(
                    child,
                    secondaryNode.NodeKey,
                  )}`"
                  type="button"
                  class="course-mega-menu__secondary-option"
                  @click="goToSecondary(child)"
                >
                  <span
                    v-if="getNodeIcon(child, secondaryNode.NodeKey)"
                    class="course-mega-menu__secondary-icon"
                  >
                    <img
                      :src="getNodeIcon(child, secondaryNode.NodeKey)"
                      :alt="getNodeTitle(child, secondaryNode.NodeKey)"
                    />
                  </span>

                  <span>
                    {{ getNodeTitle(child, secondaryNode.NodeKey) }}
                  </span>
                </button>
              </div>
            </section>
          </div>

          <div
            v-if="programCategories.length && !primaryRecords.length"
            class="course-mega-menu__empty"
          >
            No courses available.
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { appConstants } from "../common/constants";
import { programCategories as programCategoriesStoreKeys } from "../store/keys";
import cryptojs from "../plugins/crypto";

export default {
  name: "CourseMegaMenu",

  data() {
    return {
      isMegaMenuOpen: false,
      selectedCategoryId: null,
      closeTimer: null,
    };
  },

  async serverPrefetch() {
    return this.loadProgramCategories();
  },

  mounted() {
    this.loadProgramCategories();
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("click", this.onOutsideClick);
  },

  beforeDestroy() {
    this.clearCloseTimer();
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("click", this.onOutsideClick);
  },

  watch: {
    $route() {
      this.closeMegaMenu(true);
    },
  },

  computed: {
    ...mapGetters(
      programCategoriesStoreKeys.namespace,
      programCategoriesStoreKeys.getters,
    ),

    activeCategoryId() {
      if (this.selectedCategoryId != null) {
        return this.selectedCategoryId;
      }

      if (!this.getCategories || !this.getCategories.length) {
        return null;
      }

      return this.getCategories[0].ProgramCategory.ProgramCategoryId;
    },

    activeCategoryRecord() {
      if (this.activeCategoryId == null || !this.getCategories) {
        return null;
      }

      return (
        this.getCategories.find(
          (record) =>
            record.ProgramCategory &&
            record.ProgramCategory.ProgramCategoryId == this.activeCategoryId,
        ) || null
      );
    },

    categoryFlow() {
      if (
        !this.activeCategoryRecord ||
        !Array.isArray(this.activeCategoryRecord.ProgramCategoryFlow)
      ) {
        return [];
      }

      return this.activeCategoryRecord.ProgramCategoryFlow.slice().sort(
        (a, b) => Number(a.SortOrder || 0) - Number(b.SortOrder || 0),
      );
    },

    menuFlow() {
      return this.categoryFlow
        .filter(
          (node) =>
            node.NodeKey !== appConstants.enums.programCategoryFlowKey.course,
        )
        .slice(0, 2);
    },

    primaryNode() {
      return this.menuFlow[0] || null;
    },

    secondaryNode() {
      return this.menuFlow[1] || null;
    },

    primaryRecords() {
      if (!this.primaryNode || this.activeCategoryId == null) {
        return [];
      }

      return this.getNodeRecords(this.primaryNode.NodeKey, {
        categoryId: this.activeCategoryId,
        programId: null,
        boardId: null,
        groupId: null,
      });
    },

    toneClass() {
      switch (Number(this.activeCategoryId)) {
        case 2:
          return "course-mega-menu--admission";
        case 1:
          return "course-mega-menu--coaching";
        case 5:
          return "course-mega-menu--foreign";
        case 6:
          return "course-mega-menu--professional";
        default:
          return "";
      }
    },
  },

  methods: {
    ...mapActions(
      programCategoriesStoreKeys.namespace,
      programCategoriesStoreKeys.actions,
    ),

    loadProgramCategories() {
      return this.get_ProgramCategories({
        config: {
          loader: {
            show: false,
          },
        },
      });
    },

    openOnHover() {
      if (window.matchMedia("(min-width: 1200px) and (hover: hover)").matches) {
        this.openMegaMenu();
      }
    },

    toggleMegaMenu() {
      if (this.isMegaMenuOpen) this.closeMegaMenu(true);
      else this.openMegaMenu();
    },

    onOutsideClick(event) {
      if (!this.$el.contains(event.target)) this.closeMegaMenu(true);
    },

    onCategoryClick(category) {
      if (window.matchMedia("(max-width: 1199px), (hover: none)").matches) {
        this.selectCategory(category);
      } else {
        this.goToCategory(category);
      }
    },

    openMegaMenu() {
      this.clearCloseTimer();
      this.isMegaMenuOpen = true;
    },

    closeMegaMenu(resetSelection) {
      this.clearCloseTimer();
      this.isMegaMenuOpen = false;

      if (resetSelection) {
        this.selectedCategoryId = null;
      }
    },

    scheduleMegaMenuClose() {
      if (window.matchMedia("(max-width: 1199px), (hover: none)").matches)
        return;
      this.clearCloseTimer();
      this.closeTimer = setTimeout(() => {
        this.closeMegaMenu(true);
      }, 180);
    },

    cancelMegaMenuClose() {
      this.clearCloseTimer();
    },

    clearCloseTimer() {
      if (!this.closeTimer) {
        return;
      }

      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    },

    onKeyDown(event) {
      if (event.key === "Escape") {
        this.closeMegaMenu(true);
      }
    },

    selectCategory(category) {
      const categoryId = category.ProgramCategory.ProgramCategoryId;

      if (this.activeCategoryId == categoryId) {
        return;
      }

      this.selectedCategoryId = categoryId;
    },

    getNodeRecords(nodeKey, filters) {
      const filteredRecords = this.filteredRecords(
        filters.categoryId,
        filters.programId,
        filters.boardId,
        filters.groupId,
      );

      const courseRecords = this.filterByLearningType(filteredRecords);
      const ids = new Set();

      return courseRecords.filter((record) => {
        const id = this.getNodeId(record, nodeKey);

        if (id === null || id === undefined || ids.has(id)) {
          return false;
        }

        ids.add(id);
        return true;
      });
    },

    getSecondaryRecords(record) {
      if (!this.secondaryNode || !this.primaryNode || !record) {
        return [];
      }

      const filters = this.applyRecordToFilters(
        {
          categoryId: this.activeCategoryId,
          programId: null,
          boardId: null,
          groupId: null,
        },
        record,
        this.primaryNode.NodeKey,
      );

      return this.getNodeRecords(this.secondaryNode.NodeKey, filters);
    },

    getNodeId(record, nodeKey) {
      switch (nodeKey) {
        case appConstants.enums.programCategoryFlowKey.program:
          return record.AcademicProgram
            ? record.AcademicProgram.AcademicProgramId
            : null;

        case appConstants.enums.programCategoryFlowKey.board:
          return record.Board ? record.Board.BoardId : null;

        case appConstants.enums.programCategoryFlowKey.group:
          return record.Group ? record.Group.CourseGroupId : null;

        default:
          return null;
      }
    },

    getNodeTitle(record, nodeKey) {
      switch (nodeKey) {
        case appConstants.enums.programCategoryFlowKey.program:
          return record.AcademicProgram
            ? record.AcademicProgram.AcademicProgramName
            : "";

        case appConstants.enums.programCategoryFlowKey.board:
          return record.Board ? record.Board.BoardName : "";

        case appConstants.enums.programCategoryFlowKey.group:
          return record.Group
            ? record.Group.CourseGroupDisplayName ||
                record.Group.CourseGroupName
            : "";

        default:
          return "";
      }
    },

    getNodeIcon(record, nodeKey) {
      switch (nodeKey) {
        case appConstants.enums.programCategoryFlowKey.board:
          return record.Board ? record.Board.BoardIcon : "";

        case appConstants.enums.programCategoryFlowKey.group:
          return record.Group ? record.Group.CourseGroupIcon : "";

        default:
          return "";
      }
    },

    applyRecordToFilters(filters, record, nodeKey) {
      const nextFilters = Object.assign({}, filters);
      const id = this.getNodeId(record, nodeKey);

      switch (nodeKey) {
        case appConstants.enums.programCategoryFlowKey.program:
          nextFilters.programId = id;
          break;

        case appConstants.enums.programCategoryFlowKey.board:
          nextFilters.boardId = id;
          break;

        case appConstants.enums.programCategoryFlowKey.group:
          nextFilters.groupId = id;
          break;
      }

      return nextFilters;
    },

    getNextNode(nodeKey) {
      const currentIndex = this.categoryFlow.findIndex(
        (node) => node.NodeKey === nodeKey,
      );

      return currentIndex >= 0
        ? this.categoryFlow[currentIndex + 1] || null
        : null;
    },

    encryptedParam(value) {
      const actualValue =
        value === null || value === undefined ? this.defaultRouteValue : value;

      return cryptojs.UTF.encrypt(actualValue);
    },

    buildRouteParams(categoryId, filters) {
      return {
        categoryId: this.encryptedParam(categoryId),
        programId: this.encryptedParam(filters.programId),
        groupId: this.encryptedParam(filters.groupId),
        boardId: this.encryptedParam(filters.boardId),
      };
    },

    getFiltersThroughNode(record, currentNodeKey) {
      const filters = {
        programId: null,
        boardId: null,
        groupId: null,
      };

      const currentIndex = this.categoryFlow.findIndex(
        (node) => node.NodeKey === currentNodeKey,
      );

      if (currentIndex < 0) {
        return filters;
      }

      this.categoryFlow.slice(0, currentIndex + 1).forEach((node) => {
        const nextFilters = this.applyRecordToFilters(
          filters,
          record,
          node.NodeKey,
        );

        filters.programId = nextFilters.programId;
        filters.boardId = nextFilters.boardId;
        filters.groupId = nextFilters.groupId;
      });

      return filters;
    },

    navigateToNode(node, categoryId, filters) {
      if (!node) {
        return;
      }

      const routeName = this.getRouteName(node.NodeKey);

      if (!routeName) {
        return;
      }

      const params = this.buildRouteParams(categoryId, filters);

      this.closeMegaMenu(true);

      this.$router
        .push({
          name: routeName,
          params,
        })
        .catch(() => {});
    },

    goToCategory(category) {
      const categoryId = category.ProgramCategory.ProgramCategoryId;
      const categoryFlow = Array.isArray(category.ProgramCategoryFlow)
        ? category.ProgramCategoryFlow.slice().sort(
            (a, b) => Number(a.SortOrder || 0) - Number(b.SortOrder || 0),
          )
        : [];
      const firstNode = categoryFlow[0];

      if (!firstNode) {
        return;
      }

      this.closeMegaMenu(true);

      this.$router
        .push({
          name: this.getRouteName(firstNode.NodeKey),
          params: {
            categoryId: cryptojs.UTF.encrypt(categoryId),
          },
        })
        .catch(() => {});
    },

    goToPrimary(record) {
      if (!this.primaryNode) {
        return;
      }

      const categoryId =
        record.ProgramCategory &&
        record.ProgramCategory.ProgramCategoryId != null
          ? record.ProgramCategory.ProgramCategoryId
          : this.activeCategoryId;

      const filters = this.getFiltersThroughNode(
        record,
        this.primaryNode.NodeKey,
      );

      this.navigateToNode(
        this.getNextNode(this.primaryNode.NodeKey),
        categoryId,
        filters,
      );
    },

    goToSecondary(record) {
      if (!this.primaryNode || !this.secondaryNode) {
        return;
      }

      const categoryId =
        record.ProgramCategory &&
        record.ProgramCategory.ProgramCategoryId != null
          ? record.ProgramCategory.ProgramCategoryId
          : this.activeCategoryId;

      const filters = this.getFiltersThroughNode(
        record,
        this.secondaryNode.NodeKey,
      );

      this.navigateToNode(
        this.getNextNode(this.secondaryNode.NodeKey),
        categoryId,
        filters,
      );
    },
  },
};
</script>

<style lang="scss" scoped>
/* Small screens define the default: one scrollable sheet, category chips, then options. */
.course-mega-menu {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}
.course-mega-menu__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}
.course-mega-menu__trigger-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}
.course-mega-menu__panel {
  position: fixed;
  top: 80px;
  left: 50%;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  width: calc(100vw - 20px);
  max-height: calc(100vh - 92px);
  max-height: calc(100dvh - 92px);
  transform: translateX(-50%);
  overflow: hidden auto;
  border: 1px solid #d9def2;
  border-radius: 16px;
  background: #f8f8fb;
  box-shadow: 0 18px 50px rgba(23, 25, 59, 0.18);
}
.course-mega-menu__categories {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
  min-width: 0;
  padding: 12px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  border-bottom: 1px solid #e5e5ed;
  scrollbar-width: thin;
}
.course-mega-menu__category {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  flex: 0 0 auto;
  min-height: 44px;
  max-width: 210px;
  margin: 0;
  padding: 7px 12px;
  border: 1px solid #e2e2ec;
  border-radius: 24px;
  background: #fff;
  color: #28283a;
  font-family: "Poppins-Regular";
  font-size: 13px;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}
.course-mega-menu__category:hover,
.course-mega-menu__category:focus,
.course-mega-menu__category--active {
  border-color: #6265b7;
  background: #6265b7;
  color: #fff;
  outline: none;
}
.course-mega-menu__category:hover .course-mega-menu__category-title,
.course-mega-menu__category:focus .course-mega-menu__category-title,
.course-mega-menu__category--active .course-mega-menu__category-title {
  font-weight: 700;
}
.course-mega-menu__category-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  flex: 0 0 25px;
}
.course-mega-menu__category-icon img {
  display: block;
  width: 25px;
  height: 25px;
  object-fit: contain;
}
.course-mega-menu__category-title {
  display: block;
  overflow-wrap: anywhere;
}
.course-mega-menu__levels {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 14px;
}
.course-mega-menu__view-all {
  display: block;
  align-self: flex-start;
  margin: 0 0 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #5558a8;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}
.course-mega-menu__primary-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.course-mega-menu__primary-section {
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
}
.course-mega-menu__secondary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
}
.course-mega-menu__primary-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  margin: 0 0 8px;
  padding: 7px 10px;
  border: 0;
  border-radius: 10px;
  background: #e8efff;
  color: #263b83;
  text-align: left;
  cursor: pointer;
}
.course-mega-menu__primary-heading:hover .course-mega-menu__primary-label,
.course-mega-menu__primary-heading:focus .course-mega-menu__primary-label {
  color: #5558a8;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.course-mega-menu__primary-heading:focus {
  outline: 2px solid #6265b7;
  outline-offset: 2px;
}
.course-mega-menu__option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
}
.course-mega-menu__option-icon img {
  display: block;
  width: 30px;
  height: 30px;
  object-fit: contain;
}
.course-mega-menu__primary-label {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: #191a39;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  overflow-wrap: anywhere;
}
.course-mega-menu__secondary-grid {
  margin-top: 14px;
}
.course-mega-menu__secondary-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 0;
  min-height: 54px;
  margin: 0;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #222;
  font-family: "Poppins-Regular";
  font-size: 12px;
  line-height: 1.15;
  text-align: center;
  cursor: pointer;
}
.course-mega-menu__secondary-option:hover,
.course-mega-menu__secondary-option:focus {
  border-radius: 8px;
  background: #eeeefe;
  color: #45449a;
  outline: none;
}
.course-mega-menu__secondary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
}
.course-mega-menu__secondary-icon img {
  display: block;
  width: 28px;
  height: 28px;
  object-fit: contain;
}
.course-mega-menu__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #62626b;
  font-size: 14px;
}
.course-mega-menu-fade-enter-active,
.course-mega-menu-fade-leave-active {
  transition: opacity 0.12s ease;
}
.course-mega-menu-fade-enter,
.course-mega-menu-fade-leave-to {
  opacity: 0;
}

@media (min-width: 600px) and (max-width: 1199px) {
  .course-mega-menu__panel {
    width: calc(100vw - 32px);
    max-width: 720px;
    max-height: calc(100dvh - 100px);
  }
  .course-mega-menu__categories {
    flex-wrap: wrap;
    overflow: visible;
  }
  .course-mega-menu__category {
    flex: 1 1 190px;
    justify-content: flex-start;
  }
  .course-mega-menu__levels {
    padding: 20px;
  }
  .course-mega-menu__secondary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }
  .course-mega-menu__secondary-option {
    font-size: 14px;
  }
}
@media (min-width: 1200px) {
  .course-mega-menu__panel {
    top: 76px;
    display: grid;
    grid-template-columns: 290px minmax(0, 1fr);
    width: calc(100vw - 64px);
    max-width: 1240px;
    min-height: 370px;
    max-height: calc(100vh - 92px);
    overflow: hidden;
    border: 1.5px solid #5d8df4;
    border-radius: 18px;
  }
  .course-mega-menu__categories {
    flex-direction: column;
    padding: 30px 20px 28px 26px;
    overflow: auto;
    border-right: 1px solid #dedada;
    border-bottom: 0;
  }
  .course-mega-menu__category {
    display: grid;
    grid-template-columns: 50px minmax(0, 1fr);
    justify-content: initial;
    gap: 11px;
    min-height: 72px;
    max-width: none;
    margin-bottom: 4px;
    padding: 8px 10px;
    border: 0;
    border-bottom: 0;
    border-radius: 12px;
    background: transparent;
    color: #161616;
    font-size: 15px;
  }
  .course-mega-menu__category:last-child {
    border-bottom: 0;
  }
  .course-mega-menu__category:hover,
  .course-mega-menu__category:focus,
  .course-mega-menu__category--active {
    border-radius: 12px;
    background: rgba(98, 101, 183, 0.1);
    color: #161616;
  }
  .course-mega-menu__category-icon,
  .course-mega-menu__category-icon img {
    width: 50px;
    height: 50px;
  }
  .course-mega-menu__levels {
    padding: 34px;
    overflow-y: auto;
  }
  .course-mega-menu__view-all {
    display: none;
  }
  .course-mega-menu__primary-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-content: start;
    gap: 14px;
  }
  .course-mega-menu__primary-section {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
  }
  .course-mega-menu__primary-heading {
    gap: 8px;
    margin: 0 0 8px;
  }
  .course-mega-menu__primary-label {
    font-size: 14px;
  }
  .course-mega-menu__secondary-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
    margin-top: 0;
  }
  .course-mega-menu__secondary-option {
    justify-content: flex-start;
    min-height: 36px;
    padding: 6px 4px;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: #45465b;
    font-size: 13px;
    text-align: left;
  }
  .course-mega-menu__secondary-icon,
  .course-mega-menu__secondary-icon img {
    width: 24px;
    height: 24px;
    flex-basis: 24px;
  }
}
@media (min-width: 1200px) and (max-width: 1550px) {
  .course-mega-menu__panel {
    top: 72px;
    grid-template-columns: 255px minmax(0, 1fr);
    width: calc(100vw - 50px);
    max-width: 1120px;
    min-height: 345px;
  }
  .course-mega-menu__categories {
    padding: 25px 17px 24px 22px;
  }
  .course-mega-menu__levels {
    padding: 30px 28px;
  }
  .course-mega-menu__primary-label {
    font-size: 14px;
  }
}
</style>
